/**
 * Altara CHECK + ACT stages · combined slide asset
 *
 * A ~14-second cinematic walk-through that fuses the final two stages
 * of My Decisions into one narrative:
 *   Beat 1 (0-2.5s)  CHECK opens. Math cards + 9% progress bar appear.
 *   Beat 2 (2.5-4.5s) "Fits comfortably" banner locks in.
 *   Beat 3 (4.5-6.5s) Nomsa clicks "go to Act". Stage chips advance.
 *   Beat 4 (6.5-9s)  ACT presents two options. Nomsa picks "Ready to
 *                    proceed" — Altara never moves money, only records intent.
 *   Beat 5 (9-13s)   Trust Certificate seals with a live hash preview.
 *
 * Embed: /wallet/demos/altara-check-act?embed=1&autoplay=1
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Check, Sparkles, Landmark, User, CheckCircle2, Info,
  ArrowRight, RotateCcw, Play, Lock, Wallet, DollarSign, TrendingDown,
  TrendingUp, BadgeCheck, ExternalLink, HelpCircle, Hash, FileCheck2,
  Signature, Bell, Coins, CreditCard, FileText, Phone,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";

const STAGES = ["Trust", "Understand", "Check", "Act"];

const MATHS = [
  { label: "Monthly commitment", Icon: Wallet,        value: "R 7 500", tone: "navy" },
  { label: "Gross monthly income", Icon: TrendingUp,  value: "R 60 000" },
  { label: "Essential costs", Icon: TrendingDown,     value: "R 15 000" },
  { label: "Disposable income", Icon: DollarSign,     value: "R 45 000" },
];
const PCT = 17;   // 7500 / 45000
const THRESHOLD = 25;

export default function WalletAltaraCheckActDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") === "1";

  if (embed) {
    return (
      <div
        className="w-screen h-screen flex items-center justify-center px-6 py-4 overflow-hidden relative"
        style={{ background: "linear-gradient(160deg, #F5EFE6 0%, #EEF2F7 55%, #E2E8F0 100%)" }}
        data-testid="altara-check-act-embed"
      >
        <div className="w-full max-w-5xl">
          <EmbedHeader />
          <div className="mt-4">
            <CheckActStage autoplay={autoplay} />
          </div>
        </div>
        <SlideBrandMark />
      </div>
    );
  }
  return (
    <WalletShell activeTab="demos">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="altara-check-act">
        <Header />
        <DownloadStrip />
        <CheckActStage />
        <Footnote />
      </div>
    </WalletShell>
  );
}

function Header() {
  return (
    <header>
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Demo Simulation</div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold" style={{ color: NAVY }}>
        Check the fit · Record the intent
      </h1>
      <p className="text-sm text-slate-600 mt-2 max-w-3xl">
        The final two stages of My Decisions. Altara confirms the proposal
        fits Nomsa&apos;s disposable income, then records her intent — Altara is
        the trust layer, never the money mover.
      </p>
    </header>
  );
}
function EmbedHeader() {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
        Altara Consumer Intelligence
      </div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold mt-0.5" style={{ color: NAVY }}>
        Check the fit · Record the intent
      </h1>
      <div className="text-[13px] text-slate-600 mt-1">
        Context signals · Intent · Hash-chained certificate
      </div>
    </div>
  );
}
function SlideBrandMark() {
  return (
    <div className="absolute bottom-3 right-5 flex items-center gap-2 text-[10px] tracking-widest uppercase"
         style={{ color: NAVY, opacity: 0.55 }}>
      <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
        <span className="font-display font-bold text-[9px]" style={{ color: GOLD }}>A</span>
      </div>
      altara.co
    </div>
  );
}
function DownloadStrip() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Slide assets</div>
        <div className="text-sm text-slate-700 mt-0.5">Drop-in animation for a PowerPoint deck — MP4 or animated GIF.</div>
      </div>
      <a href="/altara-check-act.mp4" download data-testid="download-mp4"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold text-white"
         style={{ background: NAVY }}>Download MP4 <ArrowRight className="w-3.5 h-3.5" /></a>
      <a href="/altara-check-act.gif" download data-testid="download-gif"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold border"
         style={{ background: "white", borderColor: NAVY, color: NAVY }}>Download GIF <ArrowRight className="w-3.5 h-3.5" /></a>
      <Link to="/wallet/demos/altara-check-act?embed=1" target="_blank"
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold"
            style={{ background: `${GOLD}22`, color: NAVY }}>Open embed view <ExternalLink className="w-3.5 h-3.5" /></Link>
    </div>
  );
}
function Footnote() {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4 flex items-start gap-3">
      <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
      <div className="text-[12px] text-slate-600 leading-relaxed">
        <strong className="text-slate-800">Trust layer, not money mover.</strong> Altara never accepts a
        quote or executes a trade. Nomsa&apos;s intent — <em>More info</em> or
        <em> Ready to proceed</em> — is logged as an immutable, hash-chained
        event. The institution receives a signed Trust Certificate; the
        transaction remains with the institution.
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   CHECK + ACT — animated
   ══════════════════════════════════════════════════════════════ */

function CheckActStage({ autoplay = false }) {
  const [stage, setStage] = useState("check");   // "check" | "act" | "data" | "sealed"
  const [step, setStep]   = useState(0);         // 0 idle · 1 maths · 2 pct · 3 fits · 4 → act · 5 chose · 6 data · 7 allowed · 8 sealed
  const [dataStep, setDataStep] = useState(0);   // 0 hidden · 1 shown · 2 toggle-id · 3 toggle-fp · 4 allow-pulse · 5 allowed
  const timers = useRef([]);
  const autoplayFired = useRef(false);
  const busy = step > 0 && step < 8;

  const t = (ms, fn) => timers.current.push(setTimeout(fn, ms));
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const play = () => {
    if (busy) return;
    clearTimers();
    setStage("check"); setStep(1); setDataStep(0);
    // Beat 1 · CHECK · maths + fit banner
    t(1000, () => setStep(2));
    t(2200, () => setStep(3));
    // Beat 2 · ACT · Nomsa selects "Ready to proceed"
    t(4600, () => { setStage("act"); setStep(4); });
    t(5900, () => setStep(5));
    // Beat 3 · DATA REQUEST · scoped consent (quick transition)
    t(6600, () => { setStage("data"); setDataStep(1); });
    t(7200, () => setDataStep(2));       // Identity toggle ON
    t(7700, () => setDataStep(3));       // Financial profile toggle ON
    t(8200, () => setDataStep(4));       // Allow button pulses
    t(8700, () => setDataStep(5));       // Allow tapped
    // Beat 4 · SEALED · hash-chained certificate
    t(9400, () => { setStage("sealed"); setStep(8); });
  };
  const reset = () => {
    clearTimers();
    setStage("check"); setStep(0); setDataStep(0);
  };
  useEffect(() => {
    if (!autoplay || autoplayFired.current) return;
    autoplayFired.current = true;
    setTimeout(play, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);
  useEffect(() => () => clearTimers(), []);

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden relative"
         data-testid="check-act-stage">
      {/* Proposal header */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ background: `${NAVY}12` }}>
          <Landmark className="w-5 h-5" style={{ color: NAVY }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Investment Proposal · v1.0</div>
          <div className="font-display text-lg font-semibold leading-tight" style={{ color: NAVY }}>ABC Wealth Balanced Growth Portfolio</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Sent by <span className="font-medium text-slate-700">ABC Wealth</span> · via <span className="font-medium text-slate-700">James Smith</span></div>
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-[9px] tracking-widest uppercase text-slate-400">Commitment</div>
          <div className="font-display text-base font-semibold" style={{ color: NAVY }}>R 450 000</div>
          <div className="text-[10px] text-slate-500">over 60 months</div>
        </div>
      </div>

      {/* Stage chips */}
      <div className="px-6 pt-3">
        <StageChipsCA stage={stage} />
      </div>

      {/* Body — either CHECK content, ACT choice, or sealed certificate */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          {stage === "check" && (
            <motion.div key="check"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              data-testid="check-body">
              <CheckBody step={step} />
            </motion.div>
          )}
          {stage === "act" && (
            <motion.div key="act"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4 }}
              data-testid="act-body">
              <ActBody step={step} />
            </motion.div>
          )}
          {stage === "data" && (
            <motion.div key="data"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.35 }}
              data-testid="data-body">
              <DataRequestBody step={dataStep} />
            </motion.div>
          )}
          {stage === "sealed" && (
            <motion.div key="sealed"
              initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
              data-testid="sealed-body">
              <SealedBody />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Play controls */}
      {step === 0 && (
        <div className="px-6 pb-4 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            Check confirms the fit · Act records the intent · Certificate is sealed.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={play}
                    data-testid="play-check-act"
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] font-semibold text-white"
                    style={{ background: NAVY }}>
              <Play className="w-3.5 h-3.5" /> Play walkthrough
            </button>
            <button onClick={reset}
                    className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-[12px] font-medium border"
                    style={{ background: "white", borderColor: "#E2E8F0", color: NAVY }}
                    title="Reset">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════ Stage chips ══════════════ */
function StageChipsCA({ stage }) {
  const state = (i) => {
    if (i <= 1) return "passed";                                       // Trust, Understand
    if (i === 2) return stage === "check" ? "active" : "passed";       // Check
    if (i === 3) return stage === "act" || stage === "data" || stage === "sealed" ? "active" : "locked"; // Act
    return "locked";
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {STAGES.map((s, i) => {
        const st = state(i);
        return (
          <motion.div key={s} layout
               className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border transition"
               style={{
                 background: st === "passed" ? "#ECFDF5" : st === "active" ? NAVY : "white",
                 color:      st === "passed" ? "#065F46" : st === "active" ? "white" : "#94a3b8",
                 borderColor:st === "passed" ? "#6EE7B7" : st === "active" ? NAVY   : "#E2E8F0",
                 boxShadow:  st === "active" ? `0 0 0 3px ${GOLD}44` : "none",
               }}>
            {st === "passed" ? <BadgeCheck className="w-3.5 h-3.5" /> :
             st === "active" ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: GOLD }} /> :
             <Lock className="w-3 h-3" />}
            {s}
          </motion.div>
        );
      })}
    </div>
  );
}

/* ═══════════ CHECK body ══════════════ */
function CheckBody({ step }) {
  const barWidth = step >= 2 ? `${PCT}%` : "0%";
  return (
    <div className="space-y-4">
      {/* NAVI intro */}
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
             style={{ background: `linear-gradient(135deg, ${GOLD} 0%, #F5EFE6 100%)` }}>
          <User className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <div className="text-[13px] text-slate-700 leading-relaxed">
          <strong style={{ color: NAVY }}>Now let&apos;s see how this proposal fits YOUR context.</strong><br />
          This is a private check — I&apos;m comparing the monthly commitment against the disposable income you shared in your Financial Profile. <span className="italic">Nothing here is sent to the institution or the adviser.</span>
        </div>
      </div>

      {/* Fits comfortably banner */}
      <AnimatePresence>
        {step >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-xl border p-4 flex items-start gap-3"
            style={{ background: "#ECFDF5", borderColor: "#6EE7B7" }}
            data-testid="fits-comfortably"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                 style={{ background: "#065F46" }}>
              <Check className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <div className="flex-1">
              <div className="text-[9px] tracking-[0.24em] uppercase font-semibold" style={{ color: "#065F46" }}>
                Consumer context · Fits comfortably
              </div>
              <div className="text-[13px] font-semibold mt-0.5" style={{ color: NAVY }}>
                Based on the Financial Profile you shared, this commitment fits comfortably within your disposable income.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* The maths */}
      <div>
        <div className="text-[9px] tracking-[0.24em] uppercase font-semibold mb-2" style={{ color: GOLD }}>The maths</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {MATHS.map((m, i) => (
            <MathCard key={m.label} card={m} shown={step >= 1} delay={i * 0.1} />
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="text-slate-600">Commitment as % of disposable</span>
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: step >= 2 ? 1 : 0 }}
                       className="font-semibold" style={{ color: "#065F46" }}>{PCT}%</motion.span>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden bg-slate-100 relative">
          {/* Threshold marker at 25% */}
          <div className="absolute top-0 bottom-0 w-px bg-slate-400" style={{ left: `${THRESHOLD}%` }} />
          <motion.div className="h-full rounded-full"
                      style={{ background: "linear-gradient(90deg, #10B981 0%, #34D399 100%)" }}
                      initial={{ width: 0 }} animate={{ width: barWidth }}
                      transition={{ duration: 0.9, ease: "easeOut" }} />
        </div>
        <div className="text-[10px] text-slate-500 mt-1.5 leading-snug">
          Altara flags &ldquo;additional pressure&rdquo; when the monthly commitment is more than {THRESHOLD}% of disposable income.
          This is a context signal only — it does not stop you deciding.
        </div>
      </div>

      {/* Advance button */}
      <AnimatePresence>
        {step >= 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.5 }}
                      className="pt-1">
            <div className="rounded-full inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold text-white"
                 style={{ background: NAVY, boxShadow: `0 0 0 3px ${GOLD}44` }}>
              I&apos;ve reviewed the fit — go to Act <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MathCard({ card, shown, delay }) {
  const { label, value, Icon } = card;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: shown ? 1 : 0, y: shown ? 0 : 8 }}
      transition={{ duration: 0.3, delay: shown ? delay : 0 }}
      className="rounded-xl border border-slate-100 p-2.5 bg-white"
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3" style={{ color: NAVY }} />
        <div className="text-[9px] tracking-[0.22em] uppercase font-semibold text-slate-400 leading-tight">{label}</div>
      </div>
      <div className="font-display text-lg font-semibold mt-1" style={{ color: NAVY }}>{value}</div>
    </motion.div>
  );
}

/* ═══════════ ACT body ══════════════ */
function ActBody({ step }) {
  const chose = step >= 5;
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
             style={{ background: `linear-gradient(135deg, ${GOLD} 0%, #F5EFE6 100%)` }}>
          <User className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <div className="text-[13px] text-slate-700 leading-relaxed">
          <strong style={{ color: NAVY }}>Record what you&apos;d like to do next.</strong><br />
          Altara doesn&apos;t move money. This is just what you&apos;d like to do — the institution still owns the transaction.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <IntentOption
          Icon={HelpCircle}
          eyebrow="Option 1"
          title="I need more information"
          body="Send a signed question to your adviser. Altara logs the request as evidence — the adviser sees exactly what you asked."
          selected={false}
          dimmed={chose}
          testId="intent-more-info"
        />
        <IntentOption
          Icon={CheckCircle2}
          eyebrow="Option 2"
          title="I'm ready to proceed"
          body="Altara issues a signed intent certificate to ABC Wealth. James Smith completes the transaction on their side."
          selected={chose}
          dimmed={false}
          testId="intent-ready-to-proceed"
        />
      </div>

      {chose && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-xl border p-3 flex items-center gap-3"
          style={{ background: "#F0F9FF", borderColor: "#7DD3FC" }}
        >
          <Signature className="w-5 h-5 flex-shrink-0" style={{ color: "#0369A1" }} />
          <div className="text-[12px]" style={{ color: "#0C4A6E" }}>
            <strong>Sealing your intent…</strong> Altara is hash-chaining this decision into your Trust Wallet.
          </div>
        </motion.div>
      )}
    </div>
  );
}

function IntentOption({ Icon, eyebrow, title, body, selected, dimmed, testId }) {
  return (
    <motion.div
      layout
      animate={{
        borderColor: selected ? "#0369A1" : dimmed ? "#E2E8F0" : "#CBD5E1",
        background:  selected ? "#F0F9FF" : "white",
        opacity:     dimmed ? 0.45 : 1,
        scale:       selected ? 1.01 : 1,
      }}
      className="rounded-xl border p-3 relative"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
             style={{ background: selected ? "#DBEAFE" : `${NAVY}0F`, color: selected ? "#0369A1" : NAVY }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-[9px] tracking-[0.22em] uppercase font-semibold" style={{ color: GOLD }}>{eyebrow}</div>
      </div>
      <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{title}</div>
      <div className="text-[11px] text-slate-600 mt-1 leading-snug">{body}</div>
      {selected && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
             style={{ background: "#0369A1" }}>
          <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
        </div>
      )}
    </motion.div>
  );
}

/* ═══════════ Sealed certificate ══════════════ */
function SealedBody() {
  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border p-4 flex items-center gap-3"
        style={{ background: "#ECFDF5", borderColor: "#6EE7B7" }}
      >
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
             style={{ background: "#065F46" }}>
          <Check className="w-5 h-5 text-white" strokeWidth={3} />
        </div>
        <div className="flex-1">
          <div className="text-[9px] tracking-[0.24em] uppercase font-semibold" style={{ color: "#065F46" }}>
            Trust Certificate · sealed
          </div>
          <div className="text-[13px] font-semibold" style={{ color: NAVY }}>
            Nomsa&apos;s intent has been recorded and hash-chained into her Trust Wallet.
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.4 }}
        className="rounded-xl border border-slate-200 bg-slate-50/50 p-4"
      >
        <div className="text-[9px] tracking-[0.24em] uppercase font-semibold mb-2" style={{ color: GOLD }}>
          Certificate preview
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[9px] tracking-widest uppercase text-slate-400">Certificate ID</div>
            <div className="font-mono text-[11.5px] font-semibold mt-0.5" style={{ color: NAVY }}>
              ATC-2026-09-A7C4D9F1
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[9px] tracking-widest uppercase text-slate-400">Signed by</div>
            <div className="text-[11.5px] font-semibold mt-0.5" style={{ color: NAVY }}>
              Altara Trust Utility · Ed25519
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[9px] tracking-widest uppercase text-slate-400">Intent</div>
            <div className="text-[11.5px] font-semibold mt-0.5" style={{ color: "#065F46" }}>
              Ready to proceed
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-start gap-2">
            <Hash className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: NAVY }} />
            <div className="min-w-0 flex-1">
              <div className="text-[9px] tracking-widest uppercase text-slate-400">Hash chain</div>
              <div className="font-mono text-[10.5px] font-semibold mt-0.5 truncate" style={{ color: NAVY }}>
                8f4e…7a2c ← b1d3…9f18
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-600">
          <FileCheck2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: GOLD }} />
          Delivered to <strong className="text-slate-800">ABC Wealth</strong> and
          filed in <strong className="text-slate-800">Nomsa&apos;s Trust Wallet</strong>.
        </div>
      </motion.div>
    </div>
  );
}


/* ═══════════ Data Request · scoped consent ══════════════ */
const DATA_ITEMS = [
  { id: "identity",  label: "Identity information",             hint: "Name, ID number, date of birth",  Icon: User,       initial: false, animateOn: 2 },
  { id: "income",    label: "Income details",                    hint: "Income range, source of income",  Icon: Coins,      initial: false, animateOn: null },
  { id: "account",   label: "Account and investment information", hint: "Balances, holdings, transaction history", Icon: CreditCard, initial: true,  animateOn: null },
  { id: "fp",        label: "Financial profile",                  hint: "Risk profile, investment objectives", Icon: FileText,  initial: false, animateOn: 3 },
  { id: "contact",   label: "Contact details",                    hint: "Email, phone number, address",    Icon: Phone,      initial: false, animateOn: null },
];

function DataRequestBody({ step }) {
  const isOn = (item) => item.initial || (item.animateOn !== null && step >= item.animateOn);
  const allowTapped = step >= 5;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6 items-start"
         data-testid="data-request-body">
      {/* Left · NAVI narration */}
      <div className="space-y-3 pt-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
               style={{ background: `${GOLD}22` }}>
            <ShieldCheck className="w-4 h-4" style={{ color: NAVY }} />
          </div>
          <div>
            <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>
              Scoped consent — before your intent is recorded.
            </div>
            <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">
              ABC Wealth is requesting five slices of your Altara data.
              You decide what to release. Nothing is shared unless you
              explicitly allow it — every choice is filed as evidence in
              your Trust Wallet.
            </p>
          </div>
        </div>
        <ul className="space-y-1.5 pl-11 text-[11.5px] text-slate-600">
          <li className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5" style={{ color: "#059669" }} /> POPIA lawful basis · explicit, revocable</li>
          <li className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5" style={{ color: "#059669" }} /> Minimum-necessary — Altara doesn&apos;t pre-tick</li>
          <li className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5" style={{ color: "#059669" }} /> Hash-chained into the same certificate as the intent</li>
        </ul>
      </div>

      {/* Right · phone mock */}
      <div className="relative mx-auto" style={{ width: 320, height: 600 }}>
        <div className="absolute inset-0 rounded-[36px] shadow-2xl"
             style={{ background: "linear-gradient(150deg, #111 0%, #1a1a1a 100%)", padding: 8 }}>
          <div className="w-full h-full rounded-[30px] overflow-hidden relative" style={{ background: "#FDFCF9" }}>
            {/* Notch */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 rounded-b-2xl z-20"
                 style={{ width: 92, height: 20, background: "#0a0a0a" }} />
            {/* Status bar */}
            <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[9.5px] font-semibold" style={{ color: NAVY }}>
              <span>9:41</span>
              <span>•••</span>
            </div>
            {/* Top bar */}
            <div className="px-5 pt-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
                  <span className="font-display font-bold text-[10px]" style={{ color: GOLD }}>A</span>
                </div>
                <span className="font-display font-semibold text-[13px]" style={{ color: NAVY }}>Altara</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Bell className="w-4 h-4" style={{ color: NAVY }} />
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full" style={{ background: "#EF4444" }} />
                </div>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
                     style={{ background: NAVY }}>N</div>
              </div>
            </div>
            {/* Back */}
            <div className="px-5 pt-2 flex items-center gap-1 text-[10px] font-medium" style={{ color: NAVY }}>
              <span>‹</span> Back
            </div>
            {/* Title */}
            <div className="px-5 pt-1">
              <div className="font-display text-base font-semibold leading-tight" style={{ color: NAVY }}>Data request</div>
              <div className="text-[9.5px] text-slate-600 mt-0.5 leading-snug">
                ABC Wealth is requesting access to the following information from your Altara account.
              </div>
            </div>
            {/* Institution card */}
            <div className="mx-5 mt-1.5 rounded-lg flex items-center gap-2 p-1.5" style={{ background: "#EEF2F7" }}>
              <div className="w-7 h-7 rounded-md flex items-center justify-center bg-white text-[7px] font-bold" style={{ color: NAVY }}>ABC</div>
              <div>
                <div className="text-[10.5px] font-semibold" style={{ color: NAVY }}>ABC Wealth</div>
                <div className="text-[8px]" style={{ color: `${NAVY}88` }}>Investment management</div>
              </div>
            </div>
            <div className="px-5 pt-2 pb-0.5 text-[9.5px] font-semibold" style={{ color: NAVY }}>Information requested</div>
            <div className="px-5 space-y-0.5">
              {DATA_ITEMS.map(item => {
                const on = isOn(item);
                return (
                  <div key={item.id} className="flex items-start gap-2 py-1" data-testid={`data-item-${item.id}-${on ? "on" : "off"}`}>
                    <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "#EEF2F7" }}>
                      <item.Icon className="w-2.5 h-2.5" style={{ color: NAVY }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9.5px] font-semibold leading-tight" style={{ color: NAVY }}>{item.label}</div>
                      <div className="text-[8px] text-slate-500 leading-tight mt-0.5">{item.hint}</div>
                    </div>
                    <MiniToggle on={on} />
                  </div>
                );
              })}
            </div>
            {/* Bottom buttons */}
            <div className="absolute left-0 right-0 bottom-3 px-4 flex gap-2">
              <button className="flex-1 rounded-md py-1.5 text-[10px] font-semibold border"
                      style={{ borderColor: "#E2E8F0", color: NAVY, background: "white" }}>
                Don&apos;t allow
              </button>
              <motion.button
                animate={{
                  scale: step === 4 ? [1, 1.04, 1] : step === 5 ? 0.96 : 1,
                  background: allowTapped ? "#059669" : NAVY,
                }}
                transition={step === 4 ? { duration: 0.9, repeat: 1, ease: "easeInOut" } : { duration: 0.2 }}
                data-testid="data-request-allow"
                className="flex-1 rounded-md py-1.5 text-[10px] font-semibold text-white flex items-center justify-center gap-1"
                style={{ background: NAVY }}
              >
                {allowTapped ? <><Check className="w-2.5 h-2.5" /> Allowed</> : "Allow"}
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniToggle({ on }) {
  return (
    <div className="relative flex-shrink-0" style={{
      width: 26, height: 14, borderRadius: 7,
      background: on ? NAVY : "#E2E8F0",
      transition: "background 0.25s",
    }}>
      <motion.div animate={{ x: on ? 12 : 0 }} transition={{ duration: 0.25, ease: "easeOut" }}
                  className="absolute top-0.5 left-0.5 rounded-full bg-white shadow"
                  style={{ width: 10, height: 10 }} />
    </div>
  );
}
